import react, { Component, ReactNode } from 'react';

import UserLayout from 'src/materio/layouts/UserLayout';
import ThemeComponent from 'src/materio/@core/theme/ThemeComponent';
import { SettingsConsumer, SettingsProvider } from 'src/materio/@core/context/settingsContext';
import AuthGuard from 'src/materio/@core/components/auth/AuthGuard';
import GuestGuard from 'src/materio/@core/components/auth/GuestGuard';
import Spinner from 'src/materio/@core/components/spinner';
import WindowWrapper from 'src/materio/@core/components/window-wrapper';
import { AuthProvider } from 'src/context/AuthContext';
import { defaultACLObj } from 'src/configs/acl';
import { useAuth } from './hooks/useAuth';
import React from 'react';
import { client } from './utils/axios-client';
import { leadEndpoints } from './configs/endpoints';
import { TwilioStore } from './stores/LeadStore';
import { intitializeDevice } from './features/Twilio/TwilioBase';
import { ProblemDetails } from './types/ApiErrorResponse';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ClaimGuard from './features/common/ClaimGuard';
import { ClaimType } from './features/common/RenderIf';
import ApplicationStore from './stores/ApplicationStore';
import FallbackSpinner from 'src/materio/@core/components/spinner';
import { Backdrop, CircularProgress } from '@mui/material';
import { LoadingProvider } from './v2/context/LoadingContext';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: false,
            staleTime: 30000,
        },
    },
});

type GuardProps = {
    authGuard: boolean;
    guestGuard: boolean;
    children: ReactNode;
};

const Guard = ({ children, authGuard, guestGuard }: GuardProps) => {
    if (guestGuard) {
        return <GuestGuard fallback={<Spinner />}>{children}</GuestGuard>;
    } else if (!guestGuard && !authGuard) {
        return <>{children}</>;
    } else {
        return <AuthGuard fallback={<Spinner />}>{children}</AuthGuard>;
    }
};

const App = ({ Component }: any) => {
    const showSaving = ApplicationStore.useState((s) => s.showSaving);

    const { user } = useAuth();

    React.useEffect(() => {
        if (user !== null) {
            // do twillio stuff
            loadTwillio();
        }
    }, [user]);

    const loadTwillio = async () => {
        await client
            .get(leadEndpoints.gettwiliotoken)
            .then(async (response) => {
                TwilioStore.update((s) => {
                    s.record = response.data;
                });

                intitializeDevice(response.data.token, response.data.identity, 'trace');
            })
            .catch((err: ProblemDetails) => {
                if (err.errors) {
                    for (const [key, value] of Object.entries(err.errors)) {
                        const errorMessage = '';

                        value.forEach((message) => {
                            message += message + '\\';
                        });
                    }
                }
            });
    };

    const authGuard = Component.authGuard ?? true;

    const guestGuard = Component.guestGuard ?? false;
    const claimType = Component.claimType ?? ClaimType.None;
    const getLayout = () => {
        if (Component.getLayout) return Component.getLayout(<Component />);

        return (
            <UserLayout>
                <Component />
            </UserLayout>
        );
    };

    const aclAbilities = Component.acl ?? defaultACLObj;

    return (
        <QueryClientProvider client={queryClient}>
            <AuthProvider>
                <SettingsProvider pageSettings={{}}>
                    <SettingsConsumer>
                        {({ settings }) => {
                            return (
                                <ThemeComponent settings={settings}>
                                    <WindowWrapper>
                                        <Guard authGuard={authGuard} guestGuard={guestGuard}>
                                            <ClaimGuard claimType={claimType} guestGuard={guestGuard}>
                                                {getLayout()}
                                                {showSaving && (
                                                    <Backdrop sx={{ color: '#fff', zIndex: 2000 }} open={showSaving}>
                                                        <FallbackSpinner />
                                                    </Backdrop>
                                                )}
                                            </ClaimGuard>
                                        </Guard>
                                    </WindowWrapper>
                                </ThemeComponent>
                            );
                        }}
                    </SettingsConsumer>
                </SettingsProvider>
            </AuthProvider>
        </QueryClientProvider>
    );
};

export default App;
